import { camelToSnakeKeys, formatDateToString } from '@convenia/helpers'
import { mapCustomFieldValues } from '@convenia/common-organisms/SystemFields/helpers'
import { PERSONAL_AREA } from '../consts'
import { mapFiles } from './upload'

const mapName = (fullName) => {
  const [ name, ...last ] = fullName?.trim().split(' ') || [ '' ]

  return {
    name: name || '',
    lastName: last.join(' ') || ''
  }
}

const mapGender = (data = {}) => {
  const { gender, genderIdentities } = data

  if (gender) return gender?.id

  if (!genderIdentities || genderIdentities?.id >= 3) return null

  return genderIdentities?.id
}

export const mapPersonal = (data = {}) => ({
  ...data,
  gender: mapGender(data),
  nationalities: data?.nationalities?.map(({ id }) => +id) || [],
  birthday: data?.birthDate,
  birthStateId: data?.birthState?.id,
  birthCityId: data?.birthCity?.id,
  genderIdentityId: data?.genderIdentities?.id,
  ethnicityId: data?.ethnicity?.id,
  maritalStatus: data?.maritalStatus?.id,
  maritalStatusId: data?.maritalStatus?.id,
})

export const mapHandicap = (data = {}) => ({
  ...data,
  hasDeficiency: !!data?.id,
  disabilityId: data?.disability?.id,
})

export const mapForeign = (data = {}) => ({
  ...data,
  arrivalDate: formatDateToString(data?.arrivalDate, 'DD/MM/YYYY'),
  naturalizationDate: formatDateToString(data?.naturalizationDate, 'DD/MM/YYYY'),
  marriedToBrazilian: !!data?.marriedToBrazilian,
  hasBrazilianOffspring: !!data?.hasBrazilianOffspring,
  residenceTimeId: data?.residenceTime?.id,
  entryConditionId: data?.entryCondition?.id,
  visaId: data?.visa?.id,
  addressDescription: data?.addressDescription?.id || '',
  countryId: data?.country?.id,
})

export const mapBankAccount = (data = {}, params) => ({
  ...data,
  bankId: data?.bank?.id.toString(),
  accountType: data?.accountType?.id,
  files: mapFiles(data?.documentFiles || [], params),
  accountDigit: data?.digit,
})

export const mapEducation = (data = {}, params) => ({
  ...data,
  educationTypeId: data?.educationType?.id,
  educationType: data?.educationType,
  files: mapFiles(data?.documentFiles || [], params)
})

export const mapEmployeeInput = ({ data } = {}) => camelToSnakeKeys({
  name: mapName(data?.fullName)?.name,
  lastName: mapName(data?.fullName)?.lastName,
})

export const mapPersonalInput = ({ schema, data } = {}) => ({
  ...camelToSnakeKeys({
    id: data?.id,
    motherName: data?.motherName,
    fatherName: data?.fatherName,
    birthStateId: data?.birthStateId,
    birthCityId: data?.birthCityId,
    birthDate: formatDateToString(data?.birthday),
    ethnicityId: data?.ethnicityId,
    maritalStatusId: data?.maritalStatusId,
    genderId: data?.gender,
    genderIdentityId: data?.genderIdentityId,
    socialName: data?.socialName,
  }),
  custom_fields: mapCustomFieldValues({
    data,
    schema,
    sectionId: PERSONAL_AREA.PERSONAL,
  })
})

export const mapNationalitiesInput = ({ data } = {}) => camelToSnakeKeys({
  nationalities: data?.nationalities?.map(({ id }) => +id) || []
})

export const mapForeignInput = ({ schema, data } = {}) => ({
  ...camelToSnakeKeys({
    id: data?.id,
    visaId: data?.visaId,
    countryId: data?.countryId,
    arrivalDate: formatDateToString(data?.arrivalDate),
    naturalizationDate: formatDateToString(data?.naturalizationDate),
    marriedToBrazilian: data?.marriedToBrazilian,
    hasBrazilianOffspring: data?.hasBrazilianOffspring,
    livesInBrazil: data?.livesInBrazil,
    residenceTimeId: data?.residenceTimeId,
    entryConditionId: data?.entryConditionId,
    zipCodeForeign: data?.zipCodeForeign,
    addressDescriptionId: Number(data?.addressDescription) || null,
    foreignAddress: data?.foreignAddress,
    number: data?.number,
    complement: data?.complement,
    district: data?.district,
    city: data?.city,
  }),
  custom_fields: mapCustomFieldValues({
    data,
    schema,
    sectionId: PERSONAL_AREA.FOREIGN,
  })
})

export const mapBankAccountInput = ({ schema, data } = {}) => ({
  ...camelToSnakeKeys({
    id: data?.id,
    bankId: data?.bankId ? Number(data.bankId) : null,
    agency: data?.agency,
    account: data?.account,
    digit: data?.accountDigit,
    accountTypeId: data?.accountType,
    modality: data?.modality,
    pix: data?.pix,
  }),
  custom_fields: mapCustomFieldValues({
    data,
    schema,
    entityId: data?.entityId || undefined,
    sectionId: PERSONAL_AREA.BANK_ACCOUNTS,
  })
})

export const mapEducationInput = ({ schema, data } = {}) => ({
  ...camelToSnakeKeys({
    educationTypeId: data?.educationTypeId,
    institution: data?.institution,
    course: data?.course,
    graduationYear: data?.graduationYear ? Number(data?.graduationYear) : null,
  }),
  custom_fields: mapCustomFieldValues({
    data,
    schema,
    entityId: data?.entityId || undefined,
    sectionId: PERSONAL_AREA.EDUCATIONS
  })
})

export const mapHandicapInput = ({ schema, data } = {}) => ({
  ...camelToSnakeKeys({
    disabilityId: data?.hasDeficiency ? data?.disabilityId : null,
    observations: data?.hasDeficiency ? data?.observations : null,
  }),
  custom_fields: mapCustomFieldValues({
    data: data?.hasDeficiency ? data : {},
    schema,
    sectionId: PERSONAL_AREA.HANDICAP,
  })
})
